import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';

import { useNodeStore } from '@/stores/node-store';
import { cn } from '@/lib/utils';
import {
  LOGIC_TEMPLATES,
  insertTemplate,
  templateAvailability,
  type LogicTemplate,
} from '@/pages/workflow/helper/logic-templates';

interface LogicExamplesListProps {
  isLocked?: boolean;
  search?: string;
  /** Show only examples for this node (the node's empty state). */
  nodeId?: string;
  compact?: boolean;
  /** Where to put the example; defaults to the middle of the viewport. */
  position?: { x: number; y: number };
  onInserted?: (templateId: string) => void;
}

/**
 * Insertable examples for the logic nodes: a short list, each one a small
 * valid graph. Inserting goes through the normal store actions so the result
 * is exactly what a person could have built by hand.
 */
export function LogicExamplesList({
  isLocked = false,
  search = '',
  nodeId,
  compact = false,
  position,
  onInserted,
}: LogicExamplesListProps) {
  const groupedNodes = useNodeStore((state) => state.groupedNodes);
  const { screenToFlowPosition } = useReactFlow();
  const [open, setOpen] = useState(!compact);

  const templates = useMemo(() => {
    const query = search.trim().toLowerCase();
    return LOGIC_TEMPLATES.filter((template) => {
      if (nodeId && template.nodeId !== nodeId) return false;
      if (!query) return true;
      return (
        template.title.toLowerCase().includes(query) ||
        template.summary.toLowerCase().includes(query)
      );
    }).map((template) => ({
      template,
      availability: templateAvailability(template, groupedNodes),
    }));
  }, [groupedNodes, nodeId, search]);

  if (templates.length === 0) return null;

  const insert = (template: LogicTemplate) => {
    if (isLocked) return;
    const at =
      position ??
      screenToFlowPosition({
        x: window.innerWidth / 2 - 300,
        y: window.innerHeight / 2 - 100,
      });
    const inserted = insertTemplate(template, groupedNodes, at);
    if (inserted) onInserted?.(template.id);
  };

  return (
    <section
      className={cn('px-4', compact ? 'py-2' : 'pb-3')}
      data-testid='logic-examples'
    >
      <button
        type='button'
        className='flex w-full items-center gap-1.5 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground'
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className='size-3.5' />
        ) : (
          <ChevronRight className='size-3.5' />
        )}
        <Sparkles className='size-3.5' />
        {nodeId ? 'Start from an example' : 'Examples'}
        <span className='ml-auto font-normal normal-case'>
          {templates.length}
        </span>
      </button>
      {open ? (
        <ul className='space-y-1'>
          {templates.map(({ template, availability }) => (
            <li key={template.id}>
              <button
                type='button'
                disabled={isLocked || !availability.available}
                data-testid={`logic-example-${template.id}`}
                title={
                  availability.available
                    ? template.summary
                    : `Needs ${availability.missing.join(', ')}, which this org does not have.`
                }
                className={cn(
                  'w-full rounded-md border px-2.5 py-2 text-left transition-colors',
                  'hover:border-blue-400 hover:bg-blue-50/40 dark:hover:bg-blue-950/20',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                onClick={() => insert(template)}
              >
                <div className='text-sm font-medium'>{template.title}</div>
                {!compact ? (
                  <div className='text-xs text-muted-foreground'>
                    {template.summary}
                  </div>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
